import { useEffect, useState } from "react";
import api from "./api";
import Notification from "./Notification"; // ✅ Added

export default function Dishes() {
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [note, setNote] = useState({ message: "", type: "" }); // ✅ Added

  // create form
  const [title_en, setTitleEn] = useState("");
  const [title_de, setTitleDe] = useState("");
  const [description_en, setDescriptionEn] = useState("");
  const [description_de, setDescriptionDe] = useState("");
  const [price, setPrice] = useState("");
  const [position, setPosition] = useState(0);
  const [image, setImage] = useState(null);

  // edit form
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({
    title_en: "", title_de: "", description_en: "", description_de: "",
    price: "", position: 0, image: null
  });

  useEffect(() => { loadCats(); }, []);
  useEffect(() => { if (catId) loadDishes(catId); }, [catId]);

  async function loadCats() {
    setErr("");
    try {
      const { data } = await api.get("/api/categories");
      setCats(data);
      if (data.length && !catId) setCatId(data[0]._id);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to load categories");
      setNote({ message: "Failed to load categories", type: "error" }); // ✅ Added
    }
  }

  async function loadDishes(id) {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/api/dishes", { params: { categoryId: id } });
      setRows(data);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to load dishes");
      setNote({ message: "Failed to load dishes", type: "error" }); // ✅ Added
    } finally {
      setLoading(false);
    }
  }

  function euroToCents(euro) {
    const n = Number(String(euro).replace(",", "."));
    if (Number.isNaN(n)) return 0;
    return Math.round(n * 100);
  }
  function centsToEuro(cents) {
    return (Number(cents || 0) / 100).toFixed(2);
  }

  async function createDish(e) {
    e.preventDefault();
    setErr("");
    try {
      if (!catId) return setErr("Select a category first.");
      if (!title_en && !title_de) return setErr("Enter at least English or German title.");
      if (!price) return setErr("Price is required.");

      const fd = new FormData();
      fd.append("categoryId", catId);
      if (image) fd.append("image", image);
      if (title_en) fd.append("title_en", title_en);
      if (title_de) fd.append("title_de", title_de);
      if (description_en) fd.append("description_en", description_en);
      if (description_de) fd.append("description_de", description_de);
      fd.append("priceCents", String(euroToCents(price)));
      fd.append("position", String(position || 0));

      await api.post("/api/dishes", fd, { headers: { "Content-Type": "multipart/form-data" } });

      setTitleEn(""); setTitleDe(""); setDescriptionEn(""); setDescriptionDe("");
      setPrice(""); setPosition(0); setImage(null);
      await loadDishes(catId);
      setNote({ message: "Dish added successfully ✅", type: "success" }); // ✅ Added
    } catch (e) {
      setErr(e?.response?.data?.error || "Create failed");
      setNote({ message: "Failed to add dish", type: "error" }); // ✅ Added
    }
  }

  function startEdit(row) {
    setEditId(row._id);
    setEdit({
      title_en: row?.titleI18n?.en ?? row?.title ?? "",
      title_de: row?.titleI18n?.de ?? row?.title ?? "",
      description_en: row?.descriptionI18n?.en ?? row?.description ?? "",
      description_de: row?.descriptionI18n?.de ?? row?.description ?? "",
      price: centsToEuro(row.priceCents),
      position: row.position ?? 0,
      image: null
    });
  }

  function cancelEdit() {
    setEditId(null);
    setEdit({
      title_en: "", title_de: "", description_en: "", description_de: "",
      price: "", position: 0, image: null
    });
  }

  async function saveEdit(id) {
    setErr("");
    try {
      const fd = new FormData();
      if (edit.title_en) fd.append("title_en", edit.title_en);
      if (edit.title_de) fd.append("title_de", edit.title_de);
      if (edit.description_en) fd.append("description_en", edit.description_en);
      if (edit.description_de) fd.append("description_de", edit.description_de);
      if (edit.price !== "") fd.append("priceCents", String(euroToCents(edit.price)));
      fd.append("position", String(edit.position || 0));
      if (edit.image) fd.append("image", edit.image);

      await api.put(`/api/dishes/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      cancelEdit();
      await loadDishes(catId);
      setNote({ message: "Dish updated ✏️", type: "success" }); // ✅ Added
    } catch (e) {
      setErr(e?.response?.data?.error || "Update failed");
      setNote({ message: "Failed to update dish", type: "error" }); // ✅ Added
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this dish?")) return;
    setErr("");
    try {
      await api.delete(`/api/dishes/${id}`);
      await loadDishes(catId);
      setNote({ message: "Dish deleted 🗑️", type: "success" }); // ✅ Added
    } catch (e) {
      setErr(e?.response?.data?.error || "Delete failed");
      setNote({ message: "Delete failed", type: "error" }); // ✅ Added
    }
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#d4a017" }}>Dishes</h2>
      {err && <div style={{ color: "#b00020", fontSize: 13 }}>{err}</div>}

      {/* Category selector */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <label style={lbl}>Category</label>
        <select value={catId} onChange={(e) => setCatId(e.target.value)} style={input}>
          {cats.map((c) => {
            const en = c?.nameI18n?.en ?? c?.name ?? "";
            const de = c?.nameI18n?.de ?? c?.name ?? "";
            return <option key={c._id} value={c._id}>{en} / {de}</option>;
          })}
        </select>
      </div>

      {/* Create form */}
      <form onSubmit={createDish} style={formCard}>
        <div style={grid2}>
          <div>
            <label style={lbl}>Title (EN)*</label>
            <input value={title_en} onChange={(e) => setTitleEn(e.target.value)} style={input} placeholder="Paneer Tikka" />
          </div>
          <div>
            <label style={lbl}>Title (DE)</label>
            <input value={title_de} onChange={(e) => setTitleDe(e.target.value)} style={input} placeholder="Paneer Tikka (DE)" />
          </div>
        </div>

        <div style={grid2}>
          <div>
            <label style={lbl}>Description (EN)</label>
            <textarea value={description_en} onChange={(e) => setDescriptionEn(e.target.value)} style={inputArea} />
          </div>
          <div>
            <label style={lbl}>Description (DE)</label>
            <textarea value={description_de} onChange={(e) => setDescriptionDe(e.target.value)} style={inputArea} />
          </div>
        </div>

        <div style={grid3}>
          <div>
            <label style={lbl}>Price (€)*</label>
            <input value={price} onChange={(e) => setPrice(e.target.value)} style={input} placeholder="12.99" />
          </div>
          <div>
            <label style={lbl}>Position</label>
            <input type="number" value={position} onChange={(e) => setPosition(e.target.value)} style={input} />
          </div>
          <div>
            <label style={lbl}>Image</label>
            <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} />
          </div>
        </div>

        <button style={btnPrimary}>Add Dish</button>
        <p style={{ fontSize: 12, color: "#777", margin: 0 }}>* English title required; German optional.</p>
      </form>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={table}>
          <thead>
            <tr style={{ background: "#f6f3ee" }}>
              <th style={th}>Image</th>
              <th style={th}>Title (EN / DE)</th>
              <th style={th}>Price</th>
              <th style={th}>Position</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" style={tdCenter}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="5" style={tdCenter}>No dishes yet.</td></tr>
            ) : rows.map((row) => {
              const isEditing = editId === row._id;
              const en = row?.titleI18n?.en ?? row?.title ?? "";
              const de = row?.titleI18n?.de ?? row?.title ?? "";
              return (
                <tr key={row._id}>
                  <td style={td}>
                    {row.imageUrl ? <img src={row.imageUrl} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }} /> : "—"}
                  </td>
                  <td style={td}>
                    {isEditing ? (
                      <div style={{ display: "grid", gap: 8 }}>
                        <input value={edit.title_en} onChange={(e) => setEdit({ ...edit, title_en: e.target.value })} style={input} />
                        <input value={edit.title_de} onChange={(e) => setEdit({ ...edit, title_de: e.target.value })} style={input} />
                        <textarea value={edit.description_en} onChange={(e) => setEdit({ ...edit, description_en: e.target.value })} style={inputArea} placeholder="Desc (EN)" />
                        <textarea value={edit.description_de} onChange={(e) => setEdit({ ...edit, description_de: e.target.value })} style={inputArea} placeholder="Desc (DE)" />
                        <input type="file" accept="image/*" onChange={(e) => setEdit({ ...edit, image: e.target.files?.[0] || null })} />
                      </div>
                    ) : (
                      <div>
                        <div>{en}</div>
                        <div style={{ color: "#666", fontSize: 12 }}>{de}</div>
                      </div>
                    )}
                  </td>
                  <td style={td}>{isEditing ? (
                    <input value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} style={{ ...input, width: 90 }} />
                  ) : `€ ${centsToEuro(row.priceCents)}`}</td>
                  <td style={td}>
                    {isEditing ? (
                      <input type="number" value={edit.position} onChange={(e) => setEdit({ ...edit, position: e.target.value })} style={{ ...input, width: 80 }} />
                    ) : row.position ?? 0}
                  </td>
                  <td style={td}>
                    {isEditing ? (
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => saveEdit(row._id)} style={btnPrimary} type="button">Save</button>
                        <button onClick={cancelEdit} style={btnGhost} type="button">Cancel</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => startEdit(row)} style={btnPrimary} type="button">Edit</button>
                        <button onClick={() => remove(row._id)} style={btnDanger} type="button">Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ✅ Notification */}
      <Notification
        message={note.message}
        type={note.type}
        onClose={() => setNote({ message: "", type: "" })}
      />
    </div>
  );
}

/* ==== Styles ==== */
const lbl = { fontSize: 12, fontWeight: 500, color: "#444" };
const input = { padding: 8, border: "1px solid #ccc", borderRadius: 6, width: "100%" };
const inputArea = { ...input, minHeight: 70 };
const btnPrimary = { padding: "8px 12px", borderRadius: 6, background: "#d4a017", color: "#fff", border: "none", cursor: "pointer" };
const btnGhost = { padding: "8px 12px", borderRadius: 6, background: "#f3f3f3", color: "#222", border: "1px solid #ddd", cursor: "pointer" };
const btnDanger = { padding: "8px 12px", borderRadius: 6, background: "#c62828", color: "#fff", border: "none", cursor: "pointer" };
const th = { textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #eee", fontWeight: 700, fontSize: 13, color: "#b8860b", whiteSpace: "nowrap" };
const td = { padding: "10px 8px", borderBottom: "1px solid #f3f3f3", verticalAlign: "top", fontSize: 13, color: "#333" };
const tdCenter = { ...td, textAlign: "center", color: "#666" };
const grid2 = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 };
const grid3 = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 };
const formCard = { display: "grid", gap: 12, border: "1px solid #eee", padding: 16, borderRadius: 10, background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const table = { width: "100%", borderCollapse: "collapse", minWidth: 800, background: "#fff" };
